import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Plus, Edit2, Trash2, ShoppingBag, Eye, EyeOff, Star, Image as ImageIcon, Upload, Camera, Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TALLAS_ADULTO = ['16', '28', '30', '32', '34', '36', '38', '40', '42', '44'];
const TALLAS_NINO = ['2', '4', '6', '8', '10', '12', '14', '16', '28'];
const TALLAS_CAMISA = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const initialForm = {
    nombre: '', descripcion: '', precio: 0, marca: "Karry Jean's",
    modelo: '', categoria: '', color: '', tallas_disponibles: [],
    imagenes: [], destacado: false, activo: true, stock: 0,
    // Precios por paquete (Adulto=14 piezas, Niño=16 piezas)
    precio_paquete: 0,
    tamano_paquete: 14,  // 14 si es adulto, 16 si es niño
    segmento: 'adulto'   // 'adulto' | 'nino'
};

export const Productos = () => {
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [imageUrl, setImageUrl] = useState('');

    useEffect(() => { fetchProductos(); }, []);

    const fetchProductos = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/productos`);
            setProductos(res.data);
        } catch (e) { toast.error('Error al cargar productos'); }
        finally { setLoading(false); }
    };

    const toggleTalla = (talla) => {
        setForm(prev => ({
            ...prev,
            tallas_disponibles: prev.tallas_disponibles.includes(talla)
                ? prev.tallas_disponibles.filter(t => t !== talla)
                : [...prev.tallas_disponibles, talla]
        }));
    };

    const addImage = () => {
        if (imageUrl.trim()) {
            setForm(prev => ({ ...prev, imagenes: [...prev.imagenes, imageUrl.trim()] }));
            setImageUrl('');
        }
    };

    // Upload de imagen desde galeria, camara o archivo (mobile-friendly).
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;
        setUploading(true);
        try {
            for (const file of files) {
                if (!file.type.startsWith('image/')) {
                    toast.error(`${file.name} no es una imagen`);
                    continue;
                }
                if (file.size > 10 * 1024 * 1024) {
                    toast.error(`${file.name} excede 10MB`);
                    continue;
                }
                const fd = new FormData();
                fd.append('file', file);
                const r = await axios.post(`${API_URL}/api/uploads/imagen?folder=productos`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                const url = r.data?.url;
                if (url) {
                    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
                    setForm(prev => ({ ...prev, imagenes: [...prev.imagenes, fullUrl] }));
                }
            }
            toast.success(`${files.length} imagen(es) subida(s)`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al subir imagen');
        } finally {
            setUploading(false);
            if (event.target) event.target.value = '';
        }
    };

    const removeImage = (index) => {
        setForm(prev => ({ ...prev, imagenes: prev.imagenes.filter((_, i) => i !== index) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.precio) { toast.error('Nombre y precio son requeridos'); return; }
        try {
            if (editingId) {
                await axios.put(`${API_URL}/api/productos/${editingId}`, form);
                toast.success('Producto actualizado');
            } else {
                await axios.post(`${API_URL}/api/productos`, form);
                toast.success('Producto creado');
            }
            setModalOpen(false); setForm(initialForm); setEditingId(null); fetchProductos();
        } catch (e) { toast.error('Error al guardar'); }
    };

    const handleEdit = (prod) => {
        setForm({
            nombre: prod.nombre, descripcion: prod.descripcion || '', precio: prod.precio,
            marca: prod.marca || "Karry Jean's", modelo: prod.modelo || '',
            categoria: prod.categoria || '', color: prod.color || '',
            tallas_disponibles: prod.tallas_disponibles || [], imagenes: prod.imagenes || [],
            destacado: prod.destacado || false, activo: prod.activo !== false, stock: prod.stock || 0,
            precio_paquete: prod.precio_paquete || 0,
            tamano_paquete: prod.tamano_paquete || 14,
            segmento: prod.segmento || 'adulto'
        });
        setEditingId(prod.id); setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar producto?')) return;
        try {
            await axios.delete(`${API_URL}/api/productos/${id}`);
            toast.success('Producto eliminado'); fetchProductos();
        } catch (e) { toast.error('Error al eliminar'); }
    };

    const toggleActive = async (prod) => {
        try {
            await axios.put(`${API_URL}/api/productos/${prod.id}`, { ...prod, activo: !prod.activo });
            fetchProductos();
        } catch (e) { toast.error('Error'); }
    };

    if (loading) return <div className="flex justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    return (
        <div className="space-y-6" data-testid="productos-admin-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Productos de la Tienda</h1>
                    <p className="text-zinc-400 mt-1">Administra el catálogo de tu tienda en línea</p>
                </div>
                <Button onClick={() => { setForm(initialForm); setEditingId(null); setModalOpen(true); }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="add-producto-btn">
                    <Plus className="w-5 h-5 mr-2" /> Nuevo Producto
                </Button>
            </div>

            <Card className="border border-zinc-700">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800/50">
                                    <TableHead>Imagen</TableHead>
                                    <TableHead>Producto</TableHead>
                                    <TableHead>Precio</TableHead>
                                    <TableHead>Tallas</TableHead>
                                    <TableHead>Stock</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {productos.length > 0 ? productos.map((prod) => (
                                    <TableRow key={prod.id} data-testid={`producto-row-${prod.id}`}>
                                        <TableCell>
                                            <div className="w-12 h-12 bg-zinc-800 rounded-lg overflow-hidden">
                                                {prod.imagenes?.[0] ? (
                                                    <img src={prod.imagenes[0]} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <ImageIcon className="w-5 h-5 text-zinc-400" />
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div>
                                                <p className="font-medium text-zinc-100 flex items-center gap-2">
                                                    {prod.nombre}
                                                    {prod.destacado && <Star className="w-3 h-3 text-amber-300 fill-amber-300" />}
                                                </p>
                                                <p className="text-xs text-zinc-400">{prod.color} {prod.categoria && `| ${prod.categoria}`}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-semibold text-zinc-100">${prod.precio?.toFixed(2)}</TableCell>
                                        <TableCell>
                                            <div className="flex gap-1 flex-wrap">
                                                {prod.tallas_disponibles?.map(t => (
                                                    <span key={t} className="px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded text-xs">{t}</span>
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell>{prod.stock}</TableCell>
                                        <TableCell>
                                            <button onClick={() => toggleActive(prod)}
                                                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${prod.activo ? 'bg-emerald-400/15 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
                                                {prod.activo ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                                {prod.activo ? 'Visible' : 'Oculto'}
                                            </button>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="sm" onClick={() => handleEdit(prod)} data-testid={`edit-prod-${prod.id}`}>
                                                    <Edit2 className="w-4 h-4 text-zinc-400" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(prod.id)} data-testid={`delete-prod-${prod.id}`}>
                                                    <Trash2 className="w-4 h-4 text-red-400" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-400">No hay productos. Crea el primero.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-zinc-900" data-testid="producto-modal">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label>Nombre *</Label>
                                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Nombre del producto" data-testid="prod-nombre" />
                            </div>
                            <div>
                                <Label>Precio por pieza *</Label>
                                <Input type="number" step="0.01" value={form.precio} onChange={(e) => setForm({ ...form, precio: parseFloat(e.target.value) || 0 })} data-testid="prod-precio" />
                            </div>
                            <div>
                                <Label>Stock</Label>
                                <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} data-testid="prod-stock" />
                            </div>
                            <div className="col-span-2 border-t border-zinc-800 pt-3 mt-1">
                                <Label className="text-amber-400 text-xs uppercase tracking-wider">Venta por paquete (opcional)</Label>
                                <div className="grid grid-cols-3 gap-2 mt-1.5">
                                    <div>
                                        <Label className="text-xs text-zinc-500">Segmento</Label>
                                        <div className="inline-flex bg-zinc-800 rounded-md p-0.5 border border-zinc-700 w-full">
                                            <button
                                                type="button"
                                                onClick={() => setForm({ ...form, segmento: 'adulto', tamano_paquete: 14 })}
                                                className={`flex-1 px-2 py-1 rounded-md text-[11px] font-medium transition ${form.segmento === 'adulto' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400'}`}
                                                data-testid="prod-seg-adulto"
                                            >Adulto</button>
                                            <button
                                                type="button"
                                                onClick={() => setForm({ ...form, segmento: 'nino', tamano_paquete: 16 })}
                                                className={`flex-1 px-2 py-1 rounded-md text-[11px] font-medium transition ${form.segmento === 'nino' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400'}`}
                                                data-testid="prod-seg-nino"
                                            >Niño</button>
                                            <button
                                                type="button"
                                                onClick={() => setForm({ ...form, segmento: 'camisa', tamano_paquete: 12 })}
                                                className={`flex-1 px-2 py-1 rounded-md text-[11px] font-medium transition ${form.segmento === 'camisa' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400'}`}
                                                data-testid="prod-seg-camisa"
                                            >Camisa</button>
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-zinc-500">Piezas/paquete</Label>
                                        <Input type="number" min="1" value={form.tamano_paquete} onChange={(e) => setForm({ ...form, tamano_paquete: parseInt(e.target.value) || 0 })} data-testid="prod-tamano-paquete" />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-zinc-500">Precio paquete</Label>
                                        <Input type="number" step="0.01" min="0" value={form.precio_paquete} onChange={(e) => setForm({ ...form, precio_paquete: parseFloat(e.target.value) || 0 })} placeholder="0.00" data-testid="prod-precio-paquete" />
                                    </div>
                                </div>
                                {form.precio_paquete > 0 && form.tamano_paquete > 0 && form.precio > 0 && (
                                    <p className="text-[11px] text-zinc-500 mt-1.5">
                                        Precio unitario en paquete: <span className="text-amber-300">${(form.precio_paquete / form.tamano_paquete).toFixed(2)}</span>
                                        {' '}({Math.round((1 - form.precio_paquete / form.tamano_paquete / form.precio) * 100)}% de ahorro vs pieza)
                                    </p>
                                )}
                            </div>
                            <div className="col-span-2 border-t border-zinc-800 pt-3 mt-1" />
                            <div>
                                <Label>Color</Label>
                                <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="Azul oscuro" data-testid="prod-color" />
                            </div>
                            <div>
                                <Label>Categoría</Label>
                                <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} placeholder="Slim Fit, Recto..." data-testid="prod-categoria" />
                            </div>
                            <div>
                                <Label>Marca</Label>
                                <Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} data-testid="prod-marca" />
                            </div>
                            <div>
                                <Label>Modelo</Label>
                                <Input value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} data-testid="prod-modelo" />
                            </div>
                        </div>

                        <div>
                            <Label>Descripción</Label>
                            <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Descripción del producto" rows={2} data-testid="prod-descripcion" />
                        </div>

                        <div>
                            <Label className="mb-2 block">Tallas Disponibles <span className="text-[10px] text-zinc-500">({form.segmento === 'nino' ? 'Niño' : form.segmento === 'camisa' ? 'Camisa (letra)' : 'Adulto'})</span></Label>
                            <div className="flex gap-2 flex-wrap">
                                {(form.segmento === 'nino' ? TALLAS_NINO : form.segmento === 'camisa' ? TALLAS_CAMISA : TALLAS_ADULTO).map(t => (
                                    <button key={t} type="button" onClick={() => toggleTalla(t)}
                                        className={`min-w-[40px] h-10 px-2 rounded-lg text-sm font-medium border transition-colors ${form.tallas_disponibles.includes(t) ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:border-amber-400'}`}
                                        data-testid={`toggle-talla-${t}`}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <Label className="mb-2 block">Imágenes</Label>
                            <div className="grid grid-cols-3 gap-2 mb-2">
                                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="btn-upload-galeria" className="h-10">
                                    <Upload className="w-4 h-4 mr-1.5" /> Galería
                                </Button>
                                <Button type="button" variant="outline" onClick={() => cameraInputRef.current?.click()} disabled={uploading} data-testid="btn-upload-camara" className="h-10">
                                    <Camera className="w-4 h-4 mr-1.5" /> Cámara
                                </Button>
                                <div className="flex items-center justify-center text-xs text-zinc-500 border border-dashed border-zinc-700 rounded-md">
                                    {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Subiendo...</> : 'o pega URL ↓'}
                                </div>
                            </div>
                            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" data-testid="input-file-galeria" />
                            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileUpload} className="hidden" data-testid="input-file-camara" />
                            <div className="flex gap-2">
                                <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="Pegar URL de imagen (opcional)" data-testid="prod-image-url" />
                                <Button type="button" onClick={addImage} variant="outline" data-testid="add-image-btn">Agregar URL</Button>
                            </div>
                            {form.imagenes.length > 0 && (
                                <div className="flex gap-2 mt-3 flex-wrap">
                                    {form.imagenes.map((img, i) => (
                                        <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-zinc-700 group">
                                            <img src={img} alt="" className="w-full h-full object-cover" />
                                            <button type="button" onClick={() => removeImage(i)}
                                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                <Trash2 className="w-4 h-4 text-white" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-6">
                            <div className="flex items-center gap-2">
                                <Checkbox id="destacado" checked={form.destacado} onCheckedChange={(c) => setForm({ ...form, destacado: c })} data-testid="prod-destacado" />
                                <Label htmlFor="destacado" className="cursor-pointer">Producto destacado</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Checkbox id="activo" checked={form.activo} onCheckedChange={(c) => setForm({ ...form, activo: c })} data-testid="prod-activo" />
                                <Label htmlFor="activo" className="cursor-pointer">Visible en tienda</Label>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-producto-btn">{editingId ? 'Actualizar' : 'Crear Producto'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
